/**
 * Task signals received over /sync, keyed by entity id then slot. Lets
 * surfaces whose DTOs carry no processingData (the hierarchy tree, recents)
 * show the loader without adding the JSON column to the hottest queries.
 * Event-fed only; nothing here polls.
 */
import { create } from 'zustand';
import { readBackgroundTasks } from '@bower/shared/constants/background-task';
import {
  activityFromTasks,
  slotOf,
  type EntityActivity,
  type LiveTasks,
  type TaskSignal,
} from '../utils/entityActivity';

interface State {
  readonly tasks: LiveTasks;
  setTask: (entityId: string, task: TaskSignal) => void;
  /**
   * Optimistic seed for a task the caller just started server-side, e.g. an
   * upload's HTTP response arriving after the import job's own /sync
   * completion event. Only writes when that entity has no entry for this
   * slot, so a seed can never overwrite a real event: a stale "queued" seed
   * winning over an already-"ready" row is what left the entity icon
   * spinning until reconnect.
   */
  seedTask: (entityId: string, task: TaskSignal) => void;
  /** Removes entries so the next row fetched decides, instead of a stale event. */
  clearTasks: (entityIds: readonly string[]) => void;
}

const withSlot = (tasks: LiveTasks, entityId: string, task: TaskSignal): LiveTasks => ({
  ...tasks,
  [entityId]: { ...tasks[entityId], [slotOf(task)]: task },
});

export const useEntityActivityStore = create<State>((set, get) => ({
  tasks: {},
  setTask: (entityId, task) => set((s) => ({ tasks: withSlot(s.tasks, entityId, task) })),
  seedTask: (entityId, task) => {
    if (get().tasks[entityId]?.[slotOf(task)]) return;
    set((s) => ({ tasks: withSlot(s.tasks, entityId, task) }));
  },
  clearTasks: (entityIds) =>
    set((s) => {
      if (entityIds.length === 0) return s;
      const tasks = { ...s.tasks };
      for (const id of entityIds) delete tasks[id];
      return { tasks };
    }),
}));

/**
 * Row slots, each overridden by its live /sync signal when one arrived. The
 * event is newer than any row fetched before it — but a row fetched after it
 * is NOT guaranteed to agree: a socket gap can leave a terminal (`failed` /
 * `ready`) entry here after the row it describes has already moved on
 * elsewhere. SocketContext's reconnect handler clears every entry on
 * reconnect for exactly this reason, so a fresh fetch decides instead of a
 * stale live entry overriding it.
 */
export function useEntityActivity(
  entityId: string | undefined,
  processingData?: unknown,
): EntityActivity {
  const live = useEntityActivityStore((s) => (entityId ? s.tasks[entityId] : undefined));
  const rowSlots = readBackgroundTasks(processingData) as Readonly<Record<string, TaskSignal>>;
  return activityFromTasks(Object.values({ ...rowSlots, ...live }));
}
